/**
 * The facilitation notes chat (#1174, specs/facilitation-modules.md §9): a
 * conversation with the AI about one module's facilitation text, as an
 * alternative to editing the fields by hand. The AI replies and proposes whole
 * replacement texts per field; each proposal shows the text it would replace
 * and has its own Accept. What Accept does is the parent's business: the base
 * editor fills its draft and saves a version as usual, the per-Kuutio panel
 * saves the override at once.
 *
 * The user speaks first. A message says what to improve; an empty one asks
 * the AI for a general review. Nothing is sent before that, so opening the
 * chat costs nothing.
 *
 * Scope-agnostic: nothing here knows base from instance. The parent gives the
 * endpoint, the allowed fields, the current (possibly unsaved) texts and the
 * accept callbacks. History lives in this component and is re-sent each turn,
 * as the Systeemikartta v2 dialogues do. Reset by remounting (`key`).
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import AIProgressBar from '../Solutions/shared/AIProgressBar';
import { streamChat } from './chatStream';
import useLiveText from './useLiveText';
import { FieldProposalCard, StepsProposalCard } from './ProposalCards';
import {
  Wrap, Hint, Thread, Bubble, Muted, Composer, Input, Send, ErrorBox, Proposals, ProposalsTitle,
} from './FacilitationChat.styles';

const normalizeSteps = (list) => (Array.isArray(list) ? list : []).map((s) => ({
  title: (s.title || '').trim(),
  text: (s.text || '').trim(),
  minutes: Number.isInteger(s.minutes) ? s.minutes : null,
}));
const sameSteps = (a, b) => JSON.stringify(normalizeSteps(a)) === JSON.stringify(normalizeSteps(b));

/**
 * @param {{
 *   endpoint: string,
 *   fields: { key: string, label: string }[],
 *   draft: { [field: string]: string, steps?: object[], customized?: Record<string, boolean> },
 *   hint: string,
 *   onAccept: (field: string, text: string) => void,
 *   onAcceptSteps: (steps: { title: string, text: string, minutes: number|null }[]) => void,
 *   stepsWarning?: string|null,
 *   disabled?: boolean,
 * }} props
 */
export default function FacilitationChat({
  endpoint, fields, draft, hint, onAccept, onAcceptSteps, stepsWarning = null, disabled = false,
}) {
  const { t } = useTranslation();
  const [turns, setTurns] = useState([]);
  // field → { text, rationale, original }: latest wins; `original` is the text
  // the AI was shown, so the card keeps showing what changed after an accept.
  const [proposals, setProposals] = useState({});
  const [stepsProposal, setStepsProposal] = useState(null); // { steps, rationale, original }
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const live = useLiveText();
  // The latest draft goes with every turn without re-creating send().
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const requestBody = useCallback((text, history) => {
    const { customized = {}, steps, ...texts } = draftRef.current || {};
    return {
      message: text,
      history,
      draft: { ...Object.fromEntries(fields.map((f) => [f.key, texts[f.key] || ''])), steps: normalizeSteps(steps) },
      customized,
    };
  }, [fields]);

  const send = useCallback(async (text) => {
    setBusy(true);
    setError(null);
    const history = turns.map((x) => ({ role: x.role, text: x.text }));
    setTurns((prev) => [...prev, { role: 'user', text }]);
    const body = requestBody(text, history);
    try {
      const data = await streamChat(endpoint, body, live.onText);
      if (data.reply) setTurns((prev) => [...prev, { role: 'assistant', text: data.reply }]);
      if (data.proposals?.length) {
        setProposals((prev) => ({
          ...prev,
          ...Object.fromEntries(data.proposals.map((p) => [p.field, {
            text: p.text, rationale: p.rationale || '', original: body.draft[p.field] || '',
          }])),
        }));
      }
      if (data.stepsProposal?.length) {
        setStepsProposal({ steps: data.stepsProposal, rationale: data.stepsRationale || '', original: body.draft.steps });
      }
    } catch (err) {
      // The real failure, never an empty state: a chat that silently says
      // nothing is indistinguishable from one the AI had nothing to add to.
      setError(err.code === 'CUT_OFF' ? t('facilitationChat.cutOff') : err.message);
    } finally {
      live.clear();
      setBusy(false);
    }
  }, [endpoint, requestBody, turns, live.onText, live.clear, t]);

  const submit = (e) => {
    e.preventDefault();
    if (busy || disabled) return;
    const text = message.trim(); // empty: a general review
    setMessage('');
    send(text);
  };

  const cards = useMemo(
    () => fields.filter((f) => proposals[f.key]).map((f) => ({ ...f, ...proposals[f.key] })),
    [fields, proposals],
  );
  const liveText = live.text(['reply']);
  const started = turns.length > 0 || busy;
  // The app's AI indicator, played through once: the reply streams first and the
  // proposals are written after it, so the bar stays until the whole turn is in.
  const progressMessages = useMemo(() => [
    t('facilitationChat.progress.reading'),
    t('facilitationChat.progress.thinking'),
    t('facilitationChat.progress.writing'),
  ], [t]);

  return (
    <Wrap aria-busy={busy}>
      <Hint>{hint}</Hint>
      {started ? (
        <Thread>
          {turns.map((turn, i) => (
            // eslint-disable-next-line react/no-array-index-key -- turns are append-only
            <Bubble key={`${turn.role}-${i}`} $me={turn.role === 'user'} $general={turn.role === 'user' && !turn.text}>
              {turn.role === 'user' && !turn.text ? t('facilitationChat.generalRequest') : turn.text}
            </Bubble>
          ))}
          {busy && liveText ? <Bubble>{liveText}</Bubble> : null}
        </Thread>
      ) : null}
      {busy ? <div role="status"><AIProgressBar messages={progressMessages} sequential interval={4000} /></div> : null}
      {error ? <ErrorBox role="alert">{t('facilitationChat.error', { error })}</ErrorBox> : null}
      {disabled ? <Muted>{t('facilitationChat.lockedNote')}</Muted> : null}
      <Composer onSubmit={submit}>
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t('facilitationChat.placeholder')}
          disabled={busy || disabled}
          aria-label={t('facilitationChat.placeholder')}
          aria-describedby="facilitation-chat-empty-hint"
        />
        <Send type="submit" disabled={busy || disabled}>{t('facilitationChat.send')}</Send>
      </Composer>
      <Muted id="facilitation-chat-empty-hint">{t('facilitationChat.emptyHint')}</Muted>
      {started ? (
        <Proposals>
          <ProposalsTitle>{t('facilitationChat.proposalsTitle')}</ProposalsTitle>
          {cards.length === 0 && !stepsProposal && !busy ? <Muted>{t('facilitationChat.proposalsEmpty')}</Muted> : null}
          {cards.map((c) => (
            <FieldProposalCard
              key={c.key}
              label={c.label}
              text={c.text}
              original={c.original}
              rationale={c.rationale}
              accepted={(draft?.[c.key] || '').trim() === c.text}
              disabled={disabled}
              onAccept={() => onAccept(c.key, c.text)}
              t={t}
            />
          ))}
          {stepsProposal ? (
            <StepsProposalCard
              steps={stepsProposal.steps}
              original={stepsProposal.original}
              rationale={stepsProposal.rationale}
              accepted={sameSteps(draft?.steps, stepsProposal.steps)}
              disabled={disabled}
              warning={stepsWarning}
              onAccept={() => onAcceptSteps(normalizeSteps(stepsProposal.steps))}
              t={t}
            />
          ) : null}
        </Proposals>
      ) : null}
    </Wrap>
  );
}
